import React from 'react';
import { Box } from '@mui/material';
import { orchard } from '../theme';

/* Signature cherry-pair glyph: one node, two stems, two fruit, one leaf.
   Colors are props so the white-label demo can re-skin it live. */
export function CherryMark({
  size = 32,
  fruit = orchard.cherry,
  fruitB,
  stem = orchard.leaf,
  leaf = orchard.leaf,
  animated = false,
  style,
}) {
  const secondFruit = fruitB || fruit;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label="Cherry-Pick"
      style={style}
    >
      <path
        d="M24 7 C 20 16, 15 22, 14 31"
        stroke={stem}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeDasharray={animated ? 60 : undefined}
        style={
          animated
            ? { animation: 'cpGrowStem 1.1s cubic-bezier(0.22,1,0.36,1) both' }
            : undefined
        }
      />
      <path
        d="M24 7 C 28 15, 33 20, 34 28"
        stroke={stem}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeDasharray={animated ? 60 : undefined}
        style={
          animated
            ? {
                animation:
                  'cpGrowStem 1.1s cubic-bezier(0.22,1,0.36,1) 0.12s both',
              }
            : undefined
        }
      />
      <ellipse
        cx="29.5"
        cy="6.5"
        rx="6"
        ry="3.1"
        transform="rotate(-18 29.5 6.5)"
        fill={leaf}
      />
      <circle cx="13.5" cy="36" r="7" fill={fruit} />
      <circle cx="34.5" cy="34" r="8.2" fill={secondFruit} />
      <circle cx="11.5" cy="33.5" r="2" fill="#fff" opacity="0.55" />
      <circle cx="32" cy="31" r="2.3" fill="#fff" opacity="0.55" />
    </svg>
  );
}

/* Full-screen / inline loader: bobbing cherry pair */
export function CherrySpinner({ size = 56, label, fullScreen = false }) {
  const body = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          animation: 'cpCherryBob 1.1s ease-in-out infinite',
          lineHeight: 0,
        }}
      >
        <CherryMark size={size} />
      </Box>
      {label && (
        <Box
          component="span"
          sx={{
            fontFamily: orchard.fontMono,
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );

  if (!fullScreen) return body;
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      {body}
    </Box>
  );
}

/* Ambient drifting petals — decorative, pointer-events none */
const PETALS = [
  { left: '8%', delay: 0, dur: 16, size: 10 },
  { left: '22%', delay: 4, dur: 20, size: 7 },
  { left: '38%', delay: 9, dur: 18, size: 9 },
  { left: '55%', delay: 2, dur: 22, size: 6 },
  { left: '71%', delay: 7, dur: 17, size: 10 },
  { left: '86%', delay: 12, dur: 21, size: 8 },
];

export function PetalField({ tint = orchard.blossom }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {PETALS.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            left: p.left,
            width: p.size,
            height: p.size * 1.3,
            borderRadius: '80% 20% 70% 30% / 60% 40% 60% 40%',
            bgcolor: tint,
            border: `1px solid ${orchard.cherry}22`,
            animation: `cpPetalDrift ${p.dur}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

export default CherryMark;
